import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useFormik, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import * as chanceApi from '../../lib/api/chance';
import { Box, Button, Typography } from '@mui/material';
import ForecastBaseInfoForm from '../../components/common/base/ForecastBaseInfoForm';
import AskDialog from '../../components/common/dialog/AskDialog';
import CostCalcListForm from '../../components/chancemng/costcalc/CostCalcListForm';
import CostSummary from '../../components/chancemng/costcalc/CostSummary';
import { useDispatch, useSelector } from 'react-redux';
import { getBaseCodes } from '../../modules/auth';
import ChanceCostCalcUploadDialog from './ChanceCostCalcUploadDialog';
import { useBoolean } from '../../hooks/useBoolean';
import { validationForecast } from '../../components/common/util/ForecastValid';
import {getInitialFormValues} from '../../components/common/base/ForecastBaseInfoInit';

const ProjectForcastFormContainer = () => {
  const dispatch = useDispatch();
  const { baseCodes, user, error, loading } = useSelector(
    ({ auth, loading }) => ({
      baseCodes: auth.baseCodes,
      user: auth.user,
      error: auth.baseCodesError,
      loading: loading['auth/GET_BASECODES'],
    }),
  );
  const arrayHelperRef = useRef();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [itemCdOptions, setItemCdOptions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [message, setMessage] = useState(
    '기본사항 등록이 완료되지 못했습니다.',
  );

  const [visible, setVisible] = useState(false);
  const onCancle = () => {
    setVisible(false);
  };
  const onConfirm = () => {
    setVisible(false);
  };

  const showUpload = useBoolean();

  const checkValidation = () => {
    let valid = true;
    //원가산출 체크
    if (formik.values.costCalcs.length === 0) {
      setMessage(
        '원가 산출 제품을 추가하지 않았습니다. 원가 산출 내역을 확인하세요.',
      );
      valid = false;
    }

    return valid;
  };

  const onSelectedProducts = (data) => {
    const products = [...selectedProducts, ...data];
    const filteredProducts = _.uniqBy(products, 'id');

    filteredProducts.forEach((product) => {
      const currentList = formik.values.costCalcs;
      const lastIndex = _.findLastIndex(currentList, { partnerUuid: product.partnerUuid });

      const newRow = {
        expUuid: product.expUuid || '',
        masterTyp: 'N',
        priceUuid: product.priceUuid,
        sellPartnerUuid: product.partnerUuid,
        sellPartnerNm: product.partnerNm,
        partnerUuid: product.partnerUuid,
        partnerNm: product.partnerNm,
        productNm: product.productNm,
        productUuid: product.productUuid,
        expCnt: 1,
        unitVal: product.unitVal,
        unitValIn: product.unitValIn,
        saleVal: product.unitVal || 0,
        saleValIn: product.unitValIn || 0,
        priceVal: product.priceVal,
        buyVal: product.priceVal || 0,
        priceValIn: product.priceValIn,
        productDesc: product.productDesc,
        productUnit: product.productUnit,
        itemCd: product.itemCd,
        salesType: 'unitVal',
        purchaseType: 'priceVal'
      };

      if (lastIndex !== -1) {
        arrayHelperRef.current.insert(lastIndex + 1, newRow);
      } else {
        arrayHelperRef.current.push(newRow);
      }
    });
  };

  const handleExtProduct = () => {
    arrayHelperRef.current.push({
      masterTyp: 'E',
      priceUuid: null,
      sellPartnerUuid: '',
      sellPartnerNm: '(주)에이치씨인포',
      partnerUuid: null,
      partnerNm: null,
      productNm: '',
      productUuid: null,
      expCnt: 1,
      unitVal: 0,
      unitValIn: 0,
      saleVal: 0,
      saleValIn: 0,
      priceVal: 0,
      buyVal:  0,
      priceValIn: 0,
      productDesc: '',
      productUnit: '',
      itemCd: '기타',
      salesType: 'unitVal',
      purchaseType: 'priceVal', 
    });
  };

  const [initialValues, setInitialValues] = useState(getInitialFormValues());

  useEffect(() => {
    const baseLayout = getInitialFormValues();
    const customChance = {
      crtNm: user.usrNm, 
      crtUsr: user.usrUuid,
    };
    const merged = _.mergeWith({}, baseLayout, customChance, (objValue, srcValue, key) => {
     
      if (key === 'invoiceToBe') {
        return _.mergeWith({}, baseLayout.invoiceToBe, srcValue || {}, (o, s) => s == null ? '' : undefined);
      }
      
      if (srcValue == null) return '';
      if (_.isDate(srcValue)) return srcValue;
      if (_.isArray(srcValue)) return [...srcValue];
      
      return undefined; 
    }); 
    setInitialValues(merged);
  }, []);


// ProjectForcastFormContainer.js 내부

const memoizedInitialValues = useMemo(() => {
  // 서버 데이터가 없거나 로딩 중일 때 빈 배열 방어
  const costCalcs = initialValues?.costCalcs || [];
  
  return {
    ...initialValues,
    costCalcs: costCalcs,
    // 체크박스 값 보존
    globalSalesType: localStorage.getItem('lastSalesType') || initialValues?.globalSalesType || 'unitVal',
    globalPurchaseType: localStorage.getItem('lastPurchaseType') || initialValues?.globalPurchaseType || 'priceVal',
  };
  // [수정] initialValues 전체를 넣거나, 데이터의 로딩 상태를 알 수 있는 고유 키를 넣어야 함
}, [initialValues]); // initialValues가 서버에서 들어오면 이 로직이 다시 실행되어 값을 채움



const formik = useFormik({
  initialValues: memoizedInitialValues,
  enableReinitialize: true,
  validationSchema: validationForecast(),
  onSubmit: async (values, { setSubmitting, resetForm }) => {
    if (!checkValidation()) {
      setVisible(true);
      return;
    }
    try {
      setSubmitting(true);
      await onSave(values);
      resetForm({ values });
    } finally {
      setSubmitting(false);
    }
  }
});

const onMakeCaclData = () => {
  return formik.values.costCalcs.map((item) => {
    // 1. 기초 값 숫자 변환
    const toNum = (val) => {
      if (val === undefined || val === null || String(val).trim() === '') return 0;
      const num = Number(String(val).replace(/[^0-9.-]/g, ''));
      return isNaN(num) ? 0 : num;
    };

    const count = toNum(item.expCnt);
    const sType = item.salesType || 'unitVal';
    const pType = item.purchaseType || 'priceVal';

    const calcExVat = (val) => Math.trunc((val / 1.1) + 0.0001);
    const calcVatIn = (val) => Math.trunc((val * 1.1) + 0.0001);

    // 기준 값들 초기화
    let uVal = toNum(item.unitVal);   // 매출 단가
    let sVal = toNum(item.saleVal);   // 매출 공급가
    let sValIn = toNum(item.saleValIn); // 매출 합계

    let pVal = toNum(item.priceVal);  // 매입 단가
    let bVal = toNum(item.buyVal);    // 매입 공급가
    let bValIn = toNum(item.priceValIn); // 매입 합계

    // 2. [수정] 일반 행일 경우에만 Type에 따른 역산 로직 실행
    if (!item.isAdjustment) {
      // 매출 계산
      switch (sType) {
        case 'unitVal':
          sVal = uVal * count;
          sValIn = calcVatIn(sVal);
          break;
        case 'saleVal':
          uVal = count !== 0 ? sVal / count : 0;
          sValIn = calcVatIn(sVal);
          break;
        case 'saleValIn':
          sVal = calcExVat(sValIn);
          uVal = count !== 0 ? sVal / count : 0;
          break;
      }

      // 매입 계산
      switch (pType) {
        case 'priceVal':
          bVal = pVal * count;
          bValIn = calcVatIn(bVal);
          break;
        case 'buyVal':
          pVal = count !== 0 ? bVal / count : 0;
          bValIn = calcVatIn(bVal);
          break;
        case 'priceValIn':
          bVal = calcExVat(bValIn);
          pVal = count !== 0 ? bVal / count : 0;
          break;
      }
    } else {
      // 3. [추가] 조정 행일 경우 입력된 값 유지 및 부가세 보정만 수행
      sVal = toNum(item.saleVal);
      sValIn = toNum(item.saleValIn);
      bVal = toNum(item.buyVal);
      bValIn = toNum(item.priceValIn);
    }

    // 4. 최종 데이터 매핑
    return {
      isAdjustment: item.isAdjustment, 
      masterTyp: item.masterTyp,
      sellPartnerUuid: item.sellPartnerUuid,
      partnerUuid: item.partnerUuid,
      priceUuid: item.priceUuid,
      
      productNm: item.productNm,
      productUuid: item.productUuid,
      expCnt: count,
      productDesc: item.productDesc,
      productUnit: item.productUnit,
      itemCd: item.itemCd,

      // 금액 필드
      unitVal: uVal,
      unitValIn: calcVatIn(uVal), 
      saleVal: sVal,
      saleValIn: sValIn,

      priceVal: pVal,
      priceValIn: bValIn,
      buyVal: bVal,

      // 이익 및 이익률 (보정된 값 기준)
      saleProfit: sVal - bVal,
      saleProfitIn: sValIn - bValIn,
      salePct: sVal !== 0 ? ((sVal - bVal) / sVal) * 100 : 0,

      salesType: sType,
      purchaseType: pType
    };
  });
};


  const onSave = async (values) => {
    let dataToSubmit = {
      forecastInf: {
        prjTyp: values.prjTyp,
        prjImpt: values.prjImpt,
        bugetYn: values.bugetYn,
        cstNm: values.cstNm.trim(),
        productYn: values.productYn,
        clientCmp: values.clientCmp,
        expDay: values.expDay
        ? dayjs(values.expDay).format('YYYY-MM-DD')
        : null,
        customerCf: values.customerCf,
        vendorCmp: values.vendorCmp,
        partnerTyp: values.partnerTyp,
        salesTyp: values.salesTyp,
        forcastTxt: values.forcastTxt,
        crtUsr: values.crtUsr,
        cstGrade: 2, //2단계로 바로 등록함.
        cstStatus: '기회분석',
        procurement: values.procurement,
        ...(values.prjTyp === 'M' && {
          invoiceToBe: {
            contractStDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractStDt).format('YYYY-MM-DD') : null,
            contractEdDt: values.invoiceToBe.contractStDt ? dayjs(values.invoiceToBe.contractEdDt).format('YYYY-MM-DD') : null,
            inspectYn: values.invoiceToBe.inspectYn,
            invTo: values.invoiceToBe.invTo,
            invMonth : values.invoiceToBe.invMonth,
            invDay: values.invoiceToBe.invDay,
            colTo: values.invoiceToBe.colTo,
            colDay: values.invoiceToBe.colDay,
            colMonth : values.invoiceToBe.colMonth,
            dateSchedules : values.invoiceToBe.dateSchedules,
            lockYn: 'N',
            crtUsr: values.crtUsr,
          }       
        })
      },
      expenseList: onMakeCaclData(),
    };

    try {
      const res = await chanceApi.addChanceStep2(dataToSubmit);

      if (res.data.info.forecastInf) {
        navigate(
          `/chancemng/${res.data.info.forecastInf.prjUuid}/${res.data.info.forecastInf.cstGrade}/1`,
        );
         formik.setValues({
          ...formik.values, // 기존 입력값 유지 (필요 시)
          ...res.data.info  // 서버에서 넘겨준 최신 객체로 덮어쓰기
       });
        enqueueSnackbar(`세부 저장 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const [totalPurchase, setTotalPurchase] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [totalSalesIn, setTotalSalesIn] = useState(0);
  const [totalPriceIn, setTotalPriceIn] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalProfitIn, setTotalProfitIn] = useState(0);
  const [totalMargin, setTotalMargin] = useState(0);
  const [totalMarginIn, setTotalMarginIn] = useState(0);

  useEffect(() => {
    if (!baseCodes) {
      dispatch(getBaseCodes());
    } else {
      const filteredList = _.filter(baseCodes, {
        blcCd: 'BASE',
        mlcCd: 'PRODUCT',
        slcCd: 'CATEGORY',
        useYn: 'Y',
      }).map((item) => ({
        ...item,
        step: item.step !== '' && item.step !== null ? parseInt(item.step) : 0,
      }));
      const orderedLiist = _.orderBy(filteredList, 'step');

      setItemCdOptions(orderedLiist);
    }
  }, [baseCodes]);

  useEffect(() => {
    let purchase = 0;
    let sales = 0;
    let salesIn = 0;
    let profit = 0;
    let profitIn = 0;
    _.each(formik.values.costCalcs, (product) => {
      purchase += Number(product.buyVal);
      sales += Number(product.saleVal);
      salesIn += Number(product.saleValIn);
    });
    profit = Number(sales - purchase);
    profitIn = Number(salesIn - purchase);
    let profitTyp = ( profit > 0) ? 100 : -100;
    let profitTypIn = ( profit > 0) ? 100 : -100;

    let rate =
      profit === 0 ? 0 : (Number(sales - purchase) / Number(sales)) * profitTyp;
    let rateIn =
      profitIn === 0 ? 0 : (Number(salesIn - purchase) / Number(salesIn)) * profitTypIn;

    setTotalPurchase(purchase);
    setTotalSales(sales);
    setTotalSalesIn(salesIn);
    setTotalProfit(profit);
    setTotalProfitIn(profitIn);
    setTotalMargin(rate.toFixed(3));
    setTotalMarginIn(rateIn.toFixed(3));
  }, [formik.values.costCalcs]);

useEffect(() => {
  // 0번 행의 데이터를 안전하게 가져옴
  const firstItem = formik.values.costCalcs?.[0];
  if (!formik.values.globalSalesType) {
    // 1순위: 0번 행의 salesType, 2순위: 로컬스토리지, 3순위: 기본값
    const savedSales = firstItem?.salesType || localStorage.getItem('lastSalesType') || 'unitVal';
    formik.setFieldValue('globalSalesType', savedSales);
  }

  if (!formik.values.globalPurchaseType) {
    const savedPurchase = firstItem?.purchaseType || localStorage.getItem('lastPurchaseType') || 'priceVal';
    formik.setFieldValue('globalPurchaseType', savedPurchase);
  }
}, [formik.values.costCalcs?.length]);
  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      {/* <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          my: 1,
        }}
      >
        <Button type="submit" variant="outlined" disabled={formik.isSubmitting}>
          저장
        </Button>
      </Box> */}
      <ForecastBaseInfoForm formik={formik} />
      {formik.values.vendorCmpNm !== null &&
      formik.values.vendorCmpNm !== '' ? (
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            px: 1,
          }}
        >
          {/**
          <Box>
            <CostSummary
              totalPurchase={totalPurchase}
              totalSales={totalSales}
              totalSalesIn={totalSalesIn}
              totalProfit={totalProfit}
              totalProfitIn={totalProfitIn}
              totalMargin={totalMargin}
              totalMarginIn={totalMarginIn}
            />
          </Box>
 */}
          <FormikProvider value={formik}>
            <CostCalcListForm
              formik={formik}
              ArrayHelperRef={arrayHelperRef}
              itemCdOptions={itemCdOptions}
              selectedVendor={formik.values.vendorCmp}
              onSelectedProducts={onSelectedProducts}
              onAddEtcProduct={handleExtProduct}
            />
          </FormikProvider>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 1,
            }}
          >
            <Typography variant="subtitle2" color="error.main" component="div">
              매출처 선택 후 원가산출 제품 등록이 가능합니다.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Button
          type="submit"
          variant="outlined"
        >
          저장
        </Button>

      </Box>
      <AskDialog
        visible={visible}
        title="등록 오류"
        description={message}
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </Box>
  );
};

export default ProjectForcastFormContainer;
